import { useEffect, useState } from "react";
import { Activity, ShieldHalf, Trophy, ScanFace, Plug, Handshake, Scale, Rocket } from "lucide-react";
import { startTelemetry, trackPage } from "../../lib/telemetry";
import { PilotCoach } from "../pilot/PilotCoach";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { BrainCircuit, Cable, LifeBuoy, Menu, PiggyBank, Store, BookOpen, Globe, ShieldBan, ScrollText, Workflow, LayoutDashboard, ShieldAlert, Radar, Fingerprint, PenLine, Landmark, Palette, Users, LogOut, Lock } from "lucide-react";
import { useBrand } from "../../lib/brand";
import { useAuth } from "../../lib/auth";
import { useOnboarding } from "../../lib/onboarding";
import { NotificationBell } from "../NotificationBell";
import { Switch } from "../ui/switch";
import { ThemePicker } from "../../lib/theme";
import { GuardOverlay } from "../guard/GuardOverlay";
import { GuardProvider } from "../guard/GuardContext";
import { SalesNudge } from "../sales/SalesNudge";
import { PartnershipNav } from "../PartnershipNav";
import { EnterpriseNav } from "../enterprise/EnterpriseNav";
import { WebhookAlertBanner } from "../guard/WebhookAlertBanner";

export const NAV = [
  { to: "/app", label: "Command Center", icon: LayoutDashboard, id: "dashboard", level: 1, desc: "Your daily start: what needs action, what's at risk, and what AEGIS did for you." },
  { to: "/app/sales", label: "Sales Shield", icon: Trophy, id: "sales", level: 1, desc: "Protect every sale while you sell: live sales, unprotected buyers with one-click verification links, your protection streak, XP level and daily missions." },
  { to: "/app/connect", label: "Connect & Setup", icon: Rocket, id: "connect", level: 1, desc: "Step-by-step setup with live checks: Whop key, webhook, your website, the verification embed and a test purchase." },
  { to: "/app/whop", label: "Whop Connection", icon: Plug, id: "whop", level: 1, desc: "Connect your Whop store once: payments, members and disputes then flow into AEGIS automatically." },
  { to: "/app/verifications", label: "Video Verification", icon: ScanFace, id: "verifications", level: 1, desc: "Your hosted 'Verify your account' step: setup link, consent text and every customer recording." },
  { to: "/app/monitor", label: "Admin Monitor", icon: Activity, id: "monitor", level: 1, admin: true, desc: "Platform admin only: live health, pilot activity, errors, Whop events and the pilot case study." },
  { to: "/app/security", label: "AEGIS Guard", icon: ShieldHalf, id: "security", level: 1, desc: "Your AI security officer: live threats, fraud hunting across payments, videos and sign-ins, one-click approvals and a voice console." },
  { to: "/app/compliance", label: "Compliance", icon: Scale, id: "compliance", level: 1, desc: "Live compliance controls computed from real data, honest certification status, and customer privacy requests." },
  { to: "/app/partner", label: "Whop Partner", icon: Handshake, id: "partner", level: 1, admin: true, desc: "Platform admin only: every merchant workspace, platform totals, benchmarks and the partnership revenue model." },
  { to: "/app/manual", label: "User Manual", icon: LifeBuoy, id: "manual", level: 1, desc: "Step-by-step guide to capturing the most revenue with AEGIS: setup, prevent, intercept, defend, recover." },
  { to: "/app/merchant", label: "Merchant Portal", icon: Store, id: "merchant", level: 1, desc: "Your hub: your stats, integrations, downloadable reports and payout details." },
  { to: "/app/integrations", label: "Integration Hub", icon: Cable, id: "integrations", level: 1, desc: "Deploy AEGIS into your checkout, LMS, store, ERP and CRM in minutes: keys, snippets, live health." },
  { to: "/app/disputes", label: "Disputes", icon: ShieldAlert, id: "disputes", level: 1, desc: "Every chargeback, its AI draft and its next step. Nothing is submitted without your approval." },
  { to: "/app/intercepts", label: "Intercepts", icon: ShieldBan, id: "intercepts", level: 1, desc: "Pre-dispute alerts: resolve them before they become chargebacks. This is where you save the most." },
  { to: "/app/mari", label: "MARI · AI Brain", icon: BrainCircuit, id: "mari", level: 1, desc: "Ask MARI anything about your disputes, evidence or banks, or tell her what to do." },
  { to: "/app/playbook", label: "Playbook", icon: BookOpen, id: "playbook", level: 1, desc: "Bank-tested strategies for each reason code and issuer. Searchable." },
  { to: "/app/evidence", label: "Evidence Vault", icon: Fingerprint, id: "evidence", level: 2, desc: "Everything AEGIS collected automatically: signed, hash-chained and timestamped." },
  { to: "/app/consent", label: "Consent Capture", icon: PenLine, id: "consent", level: 2, desc: "Checkout Shield: layered, signed purchase consent that beats 'I didn't authorize this'." },
  { to: "/app/banks", label: "Issuer Intel", icon: Landmark, id: "banks", level: 3, desc: "How to win: which banks reward which evidence, their channels and appeal odds." },
  { to: "/app/risk", label: "Risk Lab", icon: Radar, id: "risk", level: 3, desc: "Score any transaction before payment. Block chargebacks before they start." },
  { to: "/app/roi", label: "Savings Engine", icon: PiggyBank, id: "roi", level: 3, desc: "What AEGIS saves you: prevented chargebacks, wins and hours, net of the 15% fee." },
  { to: "/app/ingestion", label: "Webhook Ingestion", icon: Workflow, id: "ingestion", level: 4, desc: "Technical view: raw processor webhooks, the retry queue and dead letters." },
  { to: "/app/audit", label: "Audit Log", icon: ScrollText, id: "audit", level: 4, desc: "Tamper-evident record of every action, for compliance and issuer review." },
  { to: "/app/settings", label: "White-label", icon: Palette, id: "settings", level: 4, desc: "Brand name, colors, support email and dry-run mode." },
  { to: "/app/team", label: "Team", icon: Users, id: "team", level: 1, owner: true, desc: "Invite teammates as Analyst (draft and stage) or Viewer (read-only)." },
  { to: "/", label: "Landing Page", icon: Globe, id: "landing", level: 4, desc: "" },
];
const UNLOCK_DAY = { 2: 3, 3: 5, 4: 7 };

const NavItem = ({ item }) => {
  const { to, label, icon: Icon, id } = item;
  return (
    <NavLink to={to} end={to === "/app"} data-testid={`nav-${id}`} title={item.desc}
      className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-150 border-l-2 ${
        isActive ? "border-brand bg-white/[0.04] text-white" : "border-transparent text-white/45 hover:text-white hover:bg-white/[0.02]"}`}>
      <Icon className="w-4 h-4" strokeWidth={1.5} />{label}
    </NavLink>
  );
};

const Sidebar = ({ brand, open, close }) => {
  const { isOwner, isAdmin } = useAuth();
  const ob = useOnboarding();
  const items = NAV.filter((n) => (!n.owner || isOwner) && (!n.admin || isAdmin));
  const visible = items.filter((n) => ob.showAll || n.level <= ob.level);
  const nextLevel = ob.level + 1;
  const locked = ob.showAll ? 0 : items.length - visible.length;
  return (
    <aside onClick={close} className={`fixed inset-y-0 left-0 w-64 border-r border-line bg-sidebar z-40 flex-col ${open ? "flex" : "hidden lg:flex"}`} data-testid="sidebar">
      <Link to="/app" className="flex items-center gap-3 px-4 py-3 border-b border-line group" data-testid="sidebar-logo-link">
        <img src={brand?.logo_url || "/logo.webp"} alt={`${brand?.brand_name || "AEGIS Guard"} logo`} className="w-14 h-14 object-contain drop-shadow-[0_0_14px_rgba(56,189,248,0.35)] transition-transform duration-300 group-hover:scale-105" data-testid="sidebar-logo" />
        <div className="leading-tight">
          <span className="block font-mono font-extrabold tracking-[0.25em] text-lg" data-testid="brand-name">{brand?.brand_name || "AEGIS"}</span>
          <span className="block font-mono text-[9px] tracking-[0.12em] text-[#7DD3FC]/70">{brand?.white_label ? "POWERED BY AEGIS GUARD" : "CHARGEBACK PROTECTION"}</span>
        </div>
      </Link>
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {visible.map((n) => <NavItem key={n.to} item={n} />)}
        <PartnershipNav />
        <EnterpriseNav />
        {locked > 0 && (
          <p className="flex items-start gap-2 px-3 pt-3 text-[11px] text-white/35" data-testid="nav-locked-hint">
            <Lock className="w-3 h-3 mt-0.5 shrink-0" />{locked} more module{locked > 1 ? "s" : ""} unlock on day {UNLOCK_DAY[nextLevel] || 7}, or sooner as you finish setup.
          </p>
        )}
      </nav>
      <div className="px-6 py-3 border-t border-line flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
        <span className="label-xs">Show all modules</span>
        <Switch checked={ob.showAll} onCheckedChange={ob.setShowAll} data-testid="show-all-modules-switch" />
      </div>
      <div className="px-6 py-4 border-t border-line">
        <p className="label-xs">Merchant</p>
        <p className="text-sm mt-1 truncate">{brand?.merchant_name}</p>
      </div>
    </aside>
  );
};

const UserChip = () => {
  const { user, signOut } = useAuth();
  return (
    <div className="flex items-center gap-3" data-testid="user-chip">
      <div className="hidden md:block text-right leading-tight">
        <p className="text-xs" data-testid="user-email">{user?.email}</p>
        <p className={`font-mono text-[9px] tracking-[0.18em] uppercase ${user?.role === "viewer" ? "text-[#FFB800]" : "text-brand"}`} data-testid="user-role">{user?.role}{user?.role === "viewer" && " · read-only"}</p>
      </div>
      <button onClick={signOut} title="Sign out" className="text-white/45 hover:text-white transition-colors" data-testid="logout-btn"><LogOut className="w-4 h-4" /></button>
    </div>
  );
};

export default function Shell() {
  const { brand } = useBrand();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { startTelemetry(); }, []);
  useEffect(() => { trackPage(pathname); }, [pathname]);
  const current = NAV.find((n) => (n.to === "/" ? false : n.to === "/app" ? pathname === "/app" || pathname === "/app/" : pathname.startsWith(n.to)));
  return (
    <GuardProvider>
    <div className="min-h-screen grain grid-bg overflow-x-hidden">
      {open && <div className="fixed inset-0 z-30 bg-black/70 backdrop-blur-sm lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-backdrop" />}
      <Sidebar brand={brand} open={open} close={() => setOpen(false)} />
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between px-4 lg:px-8 border-b border-white/10 bg-black/70 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-white/45 hover:text-white" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></button>
            <span className="label-xs">{brand?.brand_name || "AEGIS"} /</span>
            <span className="font-mono text-sm" data-testid="header-section">{current?.label || ({ enterprise: "Enterprise", partnership: "Partnership" }[pathname.split("/")[2]]) || "Dispute"}</span>
          </div>
          <div className="flex items-center gap-4"><ThemePicker /><NotificationBell />
            <div className="flex items-center gap-2 font-mono text-xs text-white/45" data-testid="system-status">
              <span className="live-dot" /> <span className="hidden sm:inline">DEFENSE GRID ONLINE</span>
            </div>
            <UserChip />
          </div>
        </header>
        <main className="p-4 lg:p-8 pb-24 lg:pb-24 max-w-[1600px]">
          {current?.desc && <p className="text-xs text-white/45 mb-4 max-w-3xl" data-testid="module-description">{current.desc}</p>}
          <WebhookAlertBanner />
          <SalesNudge />
          <PilotCoach />
          <Outlet />
        </main>
        {pathname !== "/app/mari" && (
          <Link to="/app/mari" data-testid="mari-dock-btn"
            className="fixed bottom-6 right-6 z-30 flex items-center gap-2 px-4 py-3 border border-brand bg-black/80 backdrop-blur-xl font-mono text-[10px] tracking-[0.18em] text-brand transition-[background-color,color,box-shadow] duration-150 hover:bg-brand hover:text-black hover:shadow-[0_0_28px_rgb(var(--brand-rgb)/0.4)]">
            <BrainCircuit className="w-4 h-4" strokeWidth={1.5} /> ASK MARI
          </Link>
        )}
        <GuardOverlay />
      </div>
    </div>
    </GuardProvider>
  );
}
